import * as React from 'react';
import { useEffect } from "react";
import { connect } from 'react-redux';
import { RouteComponentProps } from "react-router";
import { useLocation } from "react-router-dom";
import { ThunkDispatch } from "redux-thunk";
import { ca } from '@cian/analytics';
import { TDeviceType, useDeviceType } from "@cian/ui-kit";

import { getArticles } from "../../actions/articles";
import { IRoutes } from "../../types/async_props";
import { ISeoItem } from "../../types/seo_data";
import { IUser } from '../../types/user';
import { IPosts } from '../../types/posts';
import { IGlobalErrorState } from '../../types/global_error';
import { IBreadItem } from '../../types/bread_crumb';
import { setCategory } from '../../reducers/modules/articles/articles';
import { List } from '../../components/list/list';
import { LoadError } from '../../components/load_error/load_error';
import { BreadCrumbs } from '../bread_crumbs/bread_crumbs';
import { Aside } from '../aside/aside';
import { setBreadCrumb } from '../../reducers/modules/bread_crumbs/bread_crumbs';
import { saveScroll } from '../../reducers/modules/scroll/scroll';
import { IScroll } from '../../types/scroll';
import { getDiscussed } from '../../reducers/modules/discussed/discussed';
import { scrollComments } from '../../reducers/modules/scroll_comment/scroll_comment';
import { IConfig } from '../../types/config';
import { combineDataLayerObject } from '../../../app/helpers/combine_data_layer_object';
import { getBreadCrumbsNames } from '../../../app/helpers/breadcrumbs_helper';
import { getMenuRubricsData, selectRubric } from '../../../app/utils';
import { IRubric, IMenuRubrics } from '../../reducers/modules/rubrics_menu/rubrics_menu';
import { getSeoSuccess } from '../../reducers/modules/seo/seo';
import { ListItemContainer } from '../list_item';
import { IAnalytics } from "../../types/analytics";
import { IApplicationState, IThunkContext, TReduxActions } from "../../types/redux";

import s from './articles.css';

interface IProps {
  user: IUser;
  articles: IPosts;
  scroll: IScroll;
  globalError: IGlobalErrorState;
  config: IConfig;
  menuRubrics: IMenuRubrics[];
  deviceType: TDeviceType;
  analytics: IAnalytics;
  path: string,
  search: string,
}

interface IArticlesProps extends IProps {
  routeCategory: string;
}

interface IDispatchProps {
  getArticles(params: any): void;
  setCategory(category: string): void;
  setBreadCrumb(path: IBreadItem[]): void;
  getDiscussed(regionID: string): void;
  saveScroll(item: any): void;
  scrollComments(): void;
  getSeoSuccess(seo: ISeoItem): void;
}

export const setCrumbsArticles = (menuItem: IRubric, tag: string, search: string, category: string) => {
  const finitePath = menuItem ? menuItem.pathname : '/stati';

  const crumbs: IBreadItem[] = [
    { path: '/magazine' },
    {...(
      menuItem
        ? { path: menuItem.pathname, name: menuItem.name, url: menuItem.pathname }
        : { path: '' }
    )},
  ];

  if (!!tag) {
    const name = tag[0].toUpperCase() + tag.substr(1);

    crumbs.push({
      path: tag,
      url: `${finitePath}?tag=${tag}`,
      name,
    });
  } else if (!!search) {
    crumbs.push({
      path: search,
      url: `${finitePath}?search=${search}`,
      name: 'Поиск',
    });
  } else if (!!category) {
    crumbs.push({ path: category });
  }

  return crumbs;
}

const ArticlesContainer: React.FC<IArticlesProps & IDispatchProps> = props => {
  const location = useLocation();
  const { pathname } = location;
  const query = new URLSearchParams(location.search);
  const tag = query.get("tag") || '';
  const search = query.get("search") || '';
  const ordering = query.get("ordering") ? query.get("ordering") as string : '-date_publish';

  const { config, menuRubrics, routeCategory } = props;
  const deviceType = useDeviceType();
  const page = query.get("page") ? parseInt(query.get("page") as string) : 1;
  const category = routeCategory ? routeCategory : query.get("category") || '';
  const regionID = config.regionID && config.regionID !== '0' ? config.regionID : '';

  useEffect(() => {
    const menuItem: IRubric = getMenuRubricsData(menuRubrics)(selectRubric)
      .find((item: IRubric) => item.pathname.includes(pathname));

    const params = {
      page, ordering, tag, search, regionID,
      ...(menuItem && menuItem.id ? { rubricId: menuItem.id } : { category }),
    };

    const crumbs = setCrumbsArticles(menuItem, tag, search, category);

    if (typeof window !== 'undefined') {
      ca('pageviewSite', combineDataLayerObject(props.analytics, getBreadCrumbsNames(crumbs), deviceType, menuItem && 'Rubric', menuItem && menuItem.name));
    }

    props.getArticles(params);
    props.setBreadCrumb(crumbs);
    props.getDiscussed(regionID);
    props.setCategory(category);

    if (menuItem) {
      return props.getSeoSuccess({
        seoUrl: menuItem.pathname,
        seoTitle: menuItem.metaTitle,
        seoDescription: menuItem.metaDescription,
        seoKeywords: menuItem.metaKeywords,
      });
    }
  }, [tag, search, ordering, page, regionID, category, props.path, props.search]);

  function loadMore(pageN: number) {
    const { menuRubrics } = props;

    const page = pageN || 1;
    const category = props.articles.currentCategory || '';
    const regionID = props.config.regionID && props.config.regionID !== '0' ? props.config.regionID : '';

    const menuItem: IRubric = getMenuRubricsData(menuRubrics)(selectRubric)
          .find((item: IRubric) => item.pathname.includes(pathname));

    const params = {
      page, ordering, tag, search, regionID,
      ...(menuItem && menuItem.id ? { rubricId: menuItem.id } : { category }),
    };

    props.getArticles(params);
  }

  function seoHideBlock() {
    return (
      <span className={s['articles__schema']}>
        <span itemProp="headline">ЦИАН - статьи о недвижимости в Москве и России</span>
        <span itemProp="description">
          ЦИАН - информационные статьи о рынке недвижимости в России.
          Статьи об аренде и продаже квартир, офисов, домов и другой недвижимости. Статьи о новостройках.
        </span>
      </span>
    );
  }

  const {
    items,
    pages_count: pagesCount,
    count: recordsCount,
  } = props.articles;
  const { item: selectedItem } = props.scroll;

  return (
    <div className={s['articles-wrap']}>
      <div itemScope itemType="https://schema.org/Article" className={`${s['articles']} cg-col-16 cg-col-xs-16`}>
        {seoHideBlock()}
        <BreadCrumbs />
        {
          props.globalError.error ?
            <LoadError /> :
            <List
                currentUser={props.user}
                items={items}
                pagesCount={pagesCount}
                recordsCount={recordsCount}
                searchTag={tag}
                saveScroll={props.saveScroll}
                selectedItem={selectedItem}
                listItemClass={ListItemContainer}
                scrollComments={props.scrollComments}
                search={search}
                onLoadMore={(page: number) => loadMore(page)} />
        }
      </div>
      <Aside />
    </div>
  );
}

function mapStateToProps(state: IApplicationState, ownProps: RouteComponentProps): IProps {
  const {
    articles,
    scroll,
    globalError,
    user,
    config,
    menuRubrics,
    deviceType,
    analytics,
  } = state;
  const { path = '' } = ownProps.match.params as IRoutes;
  const { search = '' } = ownProps.location;

  return {
    articles,
    scroll,
    globalError,
    user,
    config,
    menuRubrics: menuRubrics.items,
    deviceType,
    analytics,
    path,
    search,
  };
}

function mapDispatchToProps(dispatch: ThunkDispatch<IApplicationState, IThunkContext, TReduxActions>): IDispatchProps {
  return {
    getArticles: async (params: any) => await dispatch(getArticles(params)),
    setCategory: (category: string) => dispatch(setCategory(category)),
    setBreadCrumb: (path: IBreadItem[]) => dispatch(setBreadCrumb(path)),
    getDiscussed: (regionID: string) => dispatch(getDiscussed(regionID)),
    saveScroll: (item: any) => dispatch(saveScroll(item)),
    scrollComments: () => dispatch(scrollComments()),
    getSeoSuccess: (seo: ISeoItem) => dispatch(getSeoSuccess(seo)),
  }
}

const mergePropsInterview = (stateProps: IProps, dispatchProps: IDispatchProps): IArticlesProps & IDispatchProps => {
  return Object.assign({routeCategory: 'interview'}, stateProps, dispatchProps)
}

const mergePropsReview = (stateProps: IProps, dispatchProps: IDispatchProps): IArticlesProps & IDispatchProps => {
  return Object.assign({routeCategory: 'review'}, stateProps, dispatchProps)
}

const mergePropsAnalytics = (stateProps: IProps, dispatchProps: IDispatchProps): IArticlesProps & IDispatchProps => {
  return Object.assign({routeCategory: 'analytics'}, stateProps, dispatchProps)
}

export const Articles = connect(mapStateToProps, mapDispatchToProps)(ArticlesContainer);
export const InterviewArticles = connect(mapStateToProps, mapDispatchToProps, mergePropsInterview)(ArticlesContainer);
export const ReviewArticles = connect(mapStateToProps, mapDispatchToProps, mergePropsReview)(ArticlesContainer);
export const AnalyticsArticles = connect(mapStateToProps, mapDispatchToProps, mergePropsAnalytics)(ArticlesContainer);
